import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbCalendarEvent } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { useApi } from "src/hooks/useApi";
import { Exam } from "src/types";

interface GoaConflict {
    codes: [string, string];
    note: string;
    date: string | null;
}

// Full 4-digit year and a weekday, spelled out, so this reads unambiguously as
// a date at a glance rather than a compact DD.MM.YY that's easy to misread.
const fmtDate = (date: string | null) =>
    date
        ? new Date(date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })
        : null;

const dayKey = (date?: string | null) => (date ? new Date(date).toISOString().slice(0, 10) : null);

export function GoaConflictWarning({
    invoiceId,
    exams = [],
    onOpenExamGroup,
}: {
    invoiceId: string;
    exams?: Exam[];
    onOpenExamGroup?: (date: string, groupExams: Exam[]) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { data } = useApi<{ conflicts: GoaConflict[] }>(`/goa/validate/${invoiceId}`);
    const conflicts = data?.conflicts ?? [];

    if (conflicts.length === 0) return null;

    return (
        <>
            <button
                type="button"
                className="goa-conflict-badge"
                onClick={() => setOpen(true)}
            >
                <TbAlertTriangle />
                {t("invoicePage.goaConflictBadge", { count: conflicts.length, defaultValue: "{{count}} mögliche(r) Abrechnungsfehler" })}
            </button>

            {open && (
                <Popup
                    title={t("invoicePage.goaConflictTitle", { defaultValue: "Mögliche Abrechnungsfehler" })}
                    onClose={() => setOpen(false)}
                    message={
                        <div className="goa-conflict-popup">
                            <p className="goa-conflict-popup__intro">
                                {t("invoicePage.goaConflictIntro", { defaultValue: "Diese GOÄ-Ziffern werden laut Gebührenordnung normalerweise nicht zusammen abgerechnet. Bitte prüfen Sie die Positionen und passen Sie sie bei Bedarf an." })}
                            </p>
                            <ul className="goa-conflict-popup__list">
                                {conflicts.map((c, i) => {
                                    const groupExams = c.date ? exams.filter((e) => dayKey(e.date) === c.date) : [];
                                    const canOpen = !!onOpenExamGroup && groupExams.length > 0 && !!groupExams[0].date;
                                    return (
                                        <li key={i}>
                                            <strong>Nr. {c.codes[0]} + Nr. {c.codes[1]}</strong>
                                            {fmtDate(c.date) ? (
                                                <span className="goa-conflict-popup__date">
                                                    <TbCalendarEvent />
                                                    {t("invoicePage.goaConflictDateLabel", { defaultValue: "Behandlungstag" })}:{" "}
                                                    {canOpen ? (
                                                        <button
                                                            type="button"
                                                            className="goa-conflict-popup__date-link"
                                                            onClick={() => {
                                                                onOpenExamGroup!(groupExams[0].date!, groupExams);
                                                                setOpen(false);
                                                            }}
                                                        >
                                                            {fmtDate(c.date)}
                                                        </button>
                                                    ) : (
                                                        fmtDate(c.date)
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="goa-conflict-popup__no-date">
                                                    <TbCalendarEvent />
                                                    {t("invoicePage.goaConflictNoDate", { defaultValue: "Kein Leistungsdatum erfasst — bitte manuell prüfen." })}
                                                </span>
                                            )}
                                            <span>{c.note}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    }
                    actions={[{ label: t("popup.cancel"), variant: "default", onClick: () => setOpen(false) }]}
                />
            )}
        </>
    );
}
